package com.neurobs

import diode.ActionResult.{ModelUpdate, NoChange}
import diode.{Action, ActionResult, Circuit, Dispatcher}
import slinky.core.FunctionalComponent
import slinky.core.facade.Hooks.{useContext, useState}
import slinky.core.facade.{React, ReactElement, SetStateHookCallback}

package object react {

	type EditControlWrapper = StringValueControlWrapper
	type CheckboxControlWrapper = SingleValueControlWrapper[Boolean]

	def includeAllIf(condition: Boolean)(children: ReactElement*): Seq[ReactElement] = {
		if (condition) children.toSeq else Seq()
	}

	def includeIf(condition: Boolean)(element: => ReactElement): ReactElement = {
		if (condition) element else null
	}

	/** Convenience Wrapper around the pair returned by React useState function
	 *
	 * @param reactState Return value of useState
	 * @tparam T
	 */
	case class StateAttribute[T](reactState: (T, SetStateHookCallback[T])) {
		def value: T = reactState._1
		def setValue(value: T): Unit = reactState._2(value)
	}

	object StateAttribute {
		/** Creats StateAttribute with set state callback that does nothing.
		 * This is primarily for React context which requires a default value
		 */
		def emptyDefault[T](initial: T): StateAttribute[T] = StateAttribute(initial, new SetStateHookCallback[T](_ => {}))
	}

	/** Utility base class for objects that provide a React context containing React hook values.
	 * This allows child components to change the state and have that trigger a change of the
	 * context. Only components that use the context will be rerendered (or are children of rerendered components.)
	 *
	 * @tparam S Type containing the React hook values
	 * @tparam T Type of value for the React hook
	 */
	trait StateContextBase[S, T] {

		/** Provides the default value for the provider if the client doesn't supply one */
		protected def defaultStateValue(): T
		/** Creates the context value given the React hook state */
		protected def makeState(a: StateAttribute[T]): S

		private val context = React.createContext[S](makeState(StateAttribute.emptyDefault(defaultStateValue())))

		private case class Props(initialValue: T, children: ReactElement*)

		private val component = FunctionalComponent[Props] { props =>
			val reactState = StateAttribute[T](useState(default = props.initialValue))
			val state = makeState(reactState)
			context.Provider(state)(props.children:_*)
		}

		/** Creates a ReactElement that wraps children elements and provides
		 * React hook state via a React context. Any changes to that state will
		 * cause the context to change. Any child components that use the context
		 * will then be rerendered. However, intermediate components that don't
		 * use the state will not be rerendered.
		 *
		 * @param initialValue Initial value of the wrapped state
		 * @param children Child components
		 * @return React component that just provides the context
		 */
		def provider(initialValue: T = defaultStateValue())(children: ReactElement*): ReactElement =
			component(Props(initialValue = initialValue, children:_*))

		/** Provides access to the context value. Any component that uses this
		 * will be rerendered when the context state changes
		 */
		def accessState(): S = useContext(context)
	}

	/** Utility base class for objects that provide a React context containing arbitrary types
	 *
	 * @tparam S Type contained in the context
	 */
	trait ContextBase[S] {

		protected def defaultStateValue(): S

		private val context = React.createContext[S](defaultStateValue())

		/** Creates a ReactElement that wraps children elements and provides
		 * a React context.
		 *
		 * @param value Value of the wrapped state
		 * @param children Child components
		 * @return React component that just provides the context
		 */
		def provider(value: S)(children: ReactElement*): ReactElement = context.Provider(value)(children:_*)

		/** Provides access to the context value. Any component that uses this
		 * will be rerendered when the context state changes
		 */
		def accessState(): S = useContext(context)
	}

	/** Value and Dispatcher for a Circuit[Model] stored in React state via useState. See
	 * CircuitState.apply for details.
	 *
	 * @param value The current value of the model
	 * @param dispatcher Dispatcher for performing actions on the model
	 */
	case class CircuitState[+Model]
	(
		value: Model,
		dispatcher: Dispatcher
	) {
		def dispatch(a: Action): Unit = dispatcher.dispatch(a)
	}

	/**
	 * This class encapsulates a Diode Circuit instance and associated model value
	 * which is stored in the React state using the useState hook.
	 * It should be used from within a Slinky control render method.
	 * Any changes to the model value by the Circuit instance will results in a
	 * rerender of the control with the model value updated.
	 *
	 * Example:
	 *
	 * 	class MyCircuit(val initialModel: MyModel) extends Circuit[MyModel] {
	 * 		...
	 * 	}
	 *
	 * 	@react object MyControl {
	 * 		type Props = Unit
	 * 		val component = FunctionalComponent[Props] { _ =>
	 * 			val state = CircuitState[MyModel](() => MyModel(), new MyCircuit(_))
	 * 			...
	 * 			... This will be rerendered if the Dispatcher changes the model value
	 * 		}
	 * 	}
	 */
	object CircuitState {
		/** Gets (or creates) React hook value storing a Circuit instance.
		 *
		 * @param generateInitialModel Function that generates the initial value of the model
		 * @param generateCircuit Function that generates a Circuit with an initial model value
		 * @return Current value of model and dispatcher for making changes
		 */
		def apply[Model <: AnyRef](generateInitialModel: () => Model, generateCircuit: Model => Circuit[Model]): CircuitState[Model] = {
			var tempSetState: Option[SetStateHookCallback[CircuitState[Model]]] = None
			val (state, setState) = useState { () =>
				val model = generateInitialModel()
				val circuit = generateCircuit(model)
				circuit.subscribe(circuit.zoom(identity))(model => tempSetState.foreach(set => set(CircuitState(model.value, circuit))))
				CircuitState(model, circuit)
			}
			tempSetState = Some(setState)
			state
		}
	}

	/**
	 * an ActionHandler that allows you to return a Option[T] for either updating
	 * the state, or no change. This cannot be used for other ActionResult options.
	 */
	abstract class SimpleActionHandler[T] {
		protected var value: T = _

		protected def handle: PartialFunction[Any, Option[T]]

		def handleAction(model: T, action: Any): Option[ActionResult[T]] = {
			value = model
			handle.lift(action) map {
				case None => NoChange
				case Some(m) => ModelUpdate(m)
			}
		}
	}
	object SimpleActionHandler {
		implicit def extractHandler[T](handler: SimpleActionHandler[T]):  (T, Any) => Option[ActionResult[T]] =
			handler.handleAction
	}

	abstract class SimpleCircuit[T <: AnyRef] extends Circuit[T] {
		protected def handle(value: T, action: Any): Option[T]

		override val actionHandler: HandlerFunction = (value, action) => {
			handle(value = value, action = action) match {
				case None => Some(NoChange)
				case Some(t) => Some(ModelUpdate(t))
			}
		}
	}

} // package object react
